import { useState, useMemo } from "react";
import { ToolsShell, TOOLS_ACCENT } from "./ToolsShell.jsx";

// Right Triangle Calculator. Given any 2 of the 6 properties of a right
// triangle (legs a, b, hypotenuse c, angles A, B, and the right angle
// C=90°), compute the rest.
//
// Cases the user can supply (besides the implicit right angle at C):
//   - Two sides:      (a, b) | (a, c) | (b, c)
//   - One side + one angle: (a, A) | (a, B) | (b, A) | (b, B) | (c, A) | (c, B)
//   - Two angles is degenerate (since A+B=90, supplying one tells you
//     both, no scale is determined). We require at least one side.

const deg2rad = (d) => (d * Math.PI) / 180;
const rad2deg = (r) => (r * 180) / Math.PI;

const parse = (s) => { const n = parseFloat(s); return isNaN(n) ? null : n; };

const solve = ({ a, b, c, A, B }) => {
  // A is the angle opposite side a; B opposite b; C=90° opposite c.
  // First, if both A and B are given, they must sum to 90.
  if (A != null && B != null && Math.abs(A + B - 90) > 0.01) return { err: "Angles A and B must sum to 90° (right triangle)." };
  // If only one angle given, derive the other.
  if (A != null && B == null) B = 90 - A;
  if (B != null && A == null) A = 90 - B;

  // Branch by what's known:
  const sides = [a, b, c].filter((v) => v != null).length;
  const angles = [A, B].filter((v) => v != null).length;

  if (sides === 0) return { err: "Provide at least one side." };
  if (sides + angles < 2) return { err: "Need at least two values besides the right angle." };

  // If two sides known, complete:
  if (sides >= 2) {
    if (a != null && b != null) c = Math.sqrt(a * a + b * b);
    else if (a != null && c != null) {
      if (a >= c) return { err: "Leg a must be smaller than hypotenuse c." };
      b = Math.sqrt(c * c - a * a);
    } else if (b != null && c != null) {
      if (b >= c) return { err: "Leg b must be smaller than hypotenuse c." };
      a = Math.sqrt(c * c - b * b);
    }
    A = rad2deg(Math.atan2(a, b));
    B = 90 - A;
    return { a, b, c, A, B };
  }

  // One side + one angle:
  if (sides === 1 && angles >= 1) {
    if (a != null) {
      // a = c sin A → c = a / sin A; b = c cos A
      c = a / Math.sin(deg2rad(A));
      b = c * Math.cos(deg2rad(A));
    } else if (b != null) {
      // b = c cos A → c = b / cos A; a = c sin A
      c = b / Math.cos(deg2rad(A));
      a = c * Math.sin(deg2rad(A));
    } else if (c != null) {
      a = c * Math.sin(deg2rad(A));
      b = c * Math.cos(deg2rad(A));
    }
    return { a, b, c, A, B };
  }

  return { err: "Unable to solve from given values." };
};

export default function RightTriangle() {
  const [aIn, setAIn] = useState("3");
  const [bIn, setBIn] = useState("4");
  const [cIn, setCIn] = useState("");
  const [AIn, setAIn2] = useState("");
  const [BIn, setBIn2] = useState("");

  const result = useMemo(() => {
    return solve({
      a: parse(aIn),
      b: parse(bIn),
      c: parse(cIn),
      A: parse(AIn),
      B: parse(BIn),
    });
  }, [aIn, bIn, cIn, AIn, BIn]);

  const fmt = (v) => (v == null || isNaN(v)) ? "—" : v.toFixed(3).replace(/\.?0+$/, "");

  const sel = {
    width: "100%", background: "#13141F", border: "1px solid #262839",
    borderRadius: 10, color: "#E8E9F3", fontSize: 15, padding: "12px 14px",
    boxSizing: "border-box",
  };
  const labelStyle = { fontSize: 13, color: "#9A9DBE", display: "block", marginBottom: 6 };

  return (
    <ToolsShell title="Right Triangle">
      <p style={{ fontSize: 14, color: "#C8CADF", marginTop: 0 }}>
        Enter any 2 values; the rest fill in automatically. Angle C is always 90° (right angle).
      </p>

      <TriangleDiagram a={result.a} b={result.b} c={result.c} A={result.A} B={result.B} />

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginTop: 18 }}>
        <div>
          <label style={labelStyle}>Leg a</label>
          <input value={aIn} onChange={(e) => setAIn(e.target.value)} inputMode="decimal" style={sel} />
        </div>
        <div>
          <label style={labelStyle}>Leg b</label>
          <input value={bIn} onChange={(e) => setBIn(e.target.value)} inputMode="decimal" style={sel} />
        </div>
        <div>
          <label style={labelStyle}>Hyp. c</label>
          <input value={cIn} onChange={(e) => setCIn(e.target.value)} inputMode="decimal" style={sel} />
        </div>
        <div>
          <label style={labelStyle}>Angle A°</label>
          <input value={AIn} onChange={(e) => setAIn2(e.target.value)} inputMode="decimal" style={sel} />
        </div>
        <div>
          <label style={labelStyle}>Angle B°</label>
          <input value={BIn} onChange={(e) => setBIn2(e.target.value)} inputMode="decimal" style={sel} />
        </div>
        <div>
          <label style={labelStyle}>Angle C°</label>
          <input value="90" disabled style={{ ...sel, opacity: 0.6, color: "#9A9DBE" }} />
        </div>
      </div>

      <div style={{ marginTop: 22, background: "#13141F", border: `1px solid ${TOOLS_ACCENT}33`, borderRadius: 12, padding: "18px 20px" }}>
        {result.err ? (
          <div style={{ color: "#F8716A", fontSize: 14 }}>{result.err}</div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
            <Readout label="a" value={fmt(result.a)} />
            <Readout label="b" value={fmt(result.b)} />
            <Readout label="c" value={fmt(result.c)} />
            <Readout label="A°" value={fmt(result.A)} />
            <Readout label="B°" value={fmt(result.B)} />
            <Readout label="Area" value={result.a != null && result.b != null ? fmt(0.5 * result.a * result.b) : "—"} />
          </div>
        )}
      </div>
      <button onClick={() => { setAIn(""); setBIn(""); setCIn(""); setAIn2(""); setBIn2(""); }} style={{ marginTop: 16, background: "#1C1E2E", color: "#9A9DBE", border: "none", borderRadius: 8, padding: "10px 18px", fontSize: 13, cursor: "pointer" }}>
        Clear all
      </button>
    </ToolsShell>
  );
}

function TriangleDiagram({ a, b, c, A, B }) {
  // Show a clean right-triangle SVG with current proportions.
  // Use a, b if valid; fall back to a sensible default.
  const aV = a && !isNaN(a) ? a : 3;
  const bV = b && !isNaN(b) ? b : 4;
  const SCALE = Math.min(150 / Math.max(aV, bV), 30);
  const aLen = aV * SCALE, bLen = bV * SCALE;
  const padding = 20;
  const W = bLen + padding * 2 + 40;
  const H = aLen + padding * 2 + 40;
  // Right angle at bottom-right corner, side a vertical, side b horizontal.
  const P1 = [padding, padding + aLen]; // bottom-left
  const P2 = [padding + bLen, padding + aLen]; // bottom-right (right angle)
  const P3 = [padding + bLen, padding]; // top-right
  return (
    <div style={{ background: "#13141F", borderRadius: 10, padding: 12, textAlign: "center" }}>
      <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`}>
        <polygon points={`${P1[0]},${P1[1]} ${P2[0]},${P2[1]} ${P3[0]},${P3[1]}`}
          fill={`${TOOLS_ACCENT}22`} stroke={TOOLS_ACCENT} strokeWidth="2" strokeLinejoin="round" />
        {/* Right-angle mark */}
        <path d={`M ${P2[0] - 10} ${P2[1]} L ${P2[0] - 10} ${P2[1] - 10} L ${P2[0]} ${P2[1] - 10}`} fill="none" stroke={TOOLS_ACCENT} strokeWidth="1.5" />
        {/* Labels */}
        <text x={(P1[0] + P2[0]) / 2} y={P1[1] + 16} fontSize="13" fill="#9A9DBE" textAnchor="middle">b</text>
        <text x={P2[0] + 12} y={(P2[1] + P3[1]) / 2} fontSize="13" fill="#9A9DBE">a</text>
        <text x={(P1[0] + P3[0]) / 2 - 14} y={(P1[1] + P3[1]) / 2 - 4} fontSize="13" fill="#9A9DBE">c</text>
        {/* Vertex labels (A B C) */}
        <text x={P1[0] - 8} y={P1[1] + 4} fontSize="11" fill={TOOLS_ACCENT} fontWeight="700">A</text>
        <text x={P2[0] + 4} y={P2[1] + 14} fontSize="11" fill={TOOLS_ACCENT} fontWeight="700">C</text>
        <text x={P3[0] + 4} y={P3[1] + 4} fontSize="11" fill={TOOLS_ACCENT} fontWeight="700">B</text>
      </svg>
    </div>
  );
}

function Readout({ label, value }) {
  return (
    <div style={{ textAlign: "center" }}>
      <div style={{ fontSize: 11, color: "#7C8099", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 700, color: TOOLS_ACCENT, fontVariantNumeric: "tabular-nums" }}>{value}</div>
    </div>
  );
}
